import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { useApplicationContext } from "../context/ApplicationContext";

interface ConfirmSwitchModalProps {
  onConfirm: () => void;
  onCancel: () => void;
}

const SWITCH_COPY: Record<"tenant" | "account" | "platform", { title: string; label: string }> = {
  tenant: { title: "Confirm Tenant Switch", label: "Tenant" },
  account: { title: "Confirm Client Switch", label: "Client" },
  platform: { title: "Confirm Platform Switch", label: "Platform" },
};

/**
 * ConfirmSwitchModal - Shared confirmation modal for tenant, client (account), and platform switching
 *
 * Warns the user about unsaved changes before switching environments. Title/body adapt to
 * `switchKind` in ApplicationContext, set by whichever selector (Tenant/Client/Platform) opened it.
 */
export const ConfirmSwitchModal: React.FC<ConfirmSwitchModalProps> = ({
  onConfirm,
  onCancel,
}) => {
  // Destructure only the state properties we need
  const { modalOpen, switchKind, pendingSwitchValue } = useApplicationContext();
  const copy = SWITCH_COPY[switchKind ?? "tenant"];

  return (
    <Dialog
      open={modalOpen}
      onClose={onCancel}
      aria-labelledby="confirm-switch-dialog-title"
      aria-describedby="confirm-switch-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="confirm-switch-dialog-title" sx={{ m: 0, mb: 3, p: 0 }}>
        {copy.title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-switch-dialog-description">
          You will lose all unsaved changes. Do you want to continue?
        </DialogContentText>
        <DialogContentText sx={{ mt: 2, fontWeight: 500 }}>
          {copy.label} will be updated to <strong>{pendingSwitchValue}</strong>
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ gap: 1}}>
        <Button onClick={onCancel} variant="outlined">
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained">
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
